import React, { useState, useEffect, useRef } from 'react';
import { correctMathExpression } from '../services/geminiService';
import toast from 'react-hot-toast';

// Fix: Use a local constant for the custom element to avoid overwriting the global JSX namespace
const MathField = 'math-field' as unknown as React.FC<React.HTMLAttributes<HTMLElement> & { ref?: React.RefObject<any> }>;

interface LatexEditorPopupProps {
  initialValue: string;
  onSave: (newValue: string) => void;
  onClose: () => void;
}

const LatexEditorPopup: React.FC<LatexEditorPopupProps> = ({ initialValue, onSave, onClose }) => {
  // Strip delimiters for MathLive, keep them for raw
  const cleanInitial = initialValue.startsWith('$$') 
    ? initialValue.slice(2, -2) 
    : (initialValue.startsWith('$') ? initialValue.slice(1, -1) : initialValue);

  const [value, setValue] = useState(cleanInitial);
  const [mode, setMode] = useState<'visual' | 'raw'>('visual');
  const [isFixing, setIsFixing] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mathFieldRef = useRef<any>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(cleanInitial);
  }, [cleanInitial]);

  // Sync MathLive value changes
  useEffect(() => {
    if (mode === 'visual' && mathFieldRef.current) {
        mathFieldRef.current.value = value;
        
        const handleInput = (evt: Event) => {
            setValue((evt.target as any).value);
        };
        
        mathFieldRef.current.addEventListener('input', handleInput);
        return () => {
            if (mathFieldRef.current) {
                mathFieldRef.current.removeEventListener('input', handleInput);
            }
        }
    } else if (mode === 'raw' && textareaRef.current) {
        // Focus textarea when switching
        textareaRef.current.focus();
    }
  }, [mode]); // Only re-run when mode switches

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        handleSave();
      }
    };
    
    const handleClickOutside = (event: MouseEvent) => {
      // MathLive creates popups/menus in body, don't close if clicking those
      const target = event.target as HTMLElement;
      if (target.closest('.ML__keyboard') || target.closest('.ML__popover')) return;

      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleSave = () => {
    // Re-wrap based on original input delimiters guess or context
    // For simplicity, we return what was passed (cleaned content). 
    // The caller (MathBlock/Inline) handles wrapping usually, but to be safe and consistent with previous behavior:
    // If we stripped delimiters, we should probably put them back if editing raw.
    // However, the MathBlock/Inline components usually expect just the content for their attributes.
    // Let's assume we return the CONTENT.
    onSave(value); 
  };
  
  const handleAutoFix = async () => {
      if (!value.trim()) return;
      setIsFixing(true);
      try {
          const corrected = await correctMathExpression(value);
          if (corrected) {
              setValue(corrected);
              if(mathFieldRef.current) mathFieldRef.current.value = corrected;
              toast.success("수식이 교정되었습니다.");
          }
      } catch (e) {
          console.error(e);
          toast.error("수식 교정에 실패했습니다.");
      } finally {
          setIsFixing(false);
      }
  };

  return (
    <div ref={modalRef} className="bg-[var(--color-bg-secondary)] border border-[var(--color-border-glass)] rounded-lg shadow-xl w-full max-w-2xl p-5 flex flex-col">
        <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-bold text-[var(--color-text-primary)]">수식 편집</h3>
            <div className="flex gap-2">
                <div className="bg-black/20 p-1 rounded-lg flex text-xs font-medium">
                    <button 
                        onClick={() => setMode('visual')}
                        className={`px-3 py-1 rounded-md transition-all ${mode === 'visual' ? 'bg-[var(--color-accent)] text-white' : 'text-[var(--color-text-secondary)] hover:text-white'}`}
                    >
                        Visual
                    </button>
                    <button 
                        onClick={() => setMode('raw')}
                        className={`px-3 py-1 rounded-md transition-all ${mode === 'raw' ? 'bg-[var(--color-accent)] text-white' : 'text-[var(--color-text-secondary)] hover:text-white'}`}
                    >
                        LaTeX
                    </button>
                </div>
            </div>
        </div>
        
        <div className="min-h-[200px] bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-md p-2 relative">
            {mode === 'visual' ? (
                <div className="w-full h-full flex items-center justify-center overflow-auto p-4">
                    <MathField 
                        ref={mathFieldRef} 
                        style={{ 
                            width: '100%', 
                            fontSize: '1.5em',
                            padding: '10px',
                            backgroundColor: 'transparent', 
                            color: 'var(--color-text-primary)',
                            border: 'none'
                        }}
                    >
                        {value}
                    </MathField>
                </div>
            ) : (
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    className="w-full h-40 bg-transparent border-none font-mono text-sm focus:outline-none resize-none text-[var(--color-text-primary)]"
                    placeholder="LaTeX 코드를 입력하세요..."
                />
            )}
        </div>

        <div className="mt-4 flex justify-between items-center">
             <button 
                onClick={handleAutoFix} 
                disabled={isFixing}
                className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-50"
            >
                {isFixing ? '분석 중...' : '✨ AI 자동 교정'}
            </button>

            <div className="flex gap-3 items-center">
                <span className="text-xs text-[var(--color-text-secondary)]">Ctrl+Enter로 저장</span>
                <button onClick={onClose} className="px-4 py-2 rounded-md font-bold transition-colors bg-[var(--color-button-secondary-bg)] hover:bg-[var(--color-button-secondary-hover)] text-white">
                    취소
                </button>
                <button onClick={handleSave} className="px-4 py-2 rounded-md font-bold transition-colors bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-[var(--color-accent-text)]">
                    저장
                </button>
            </div>
        </div>
    </div>
  );
};

export default LatexEditorPopup;